/* eslint-disable react/no-unknown-property */
import { useDeferredValue, useMemo } from 'react';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Edges, Text } from '@react-three/drei';
import { getBoxMeshData } from './BoxMesh';

const UNIT_DIVISOR = 2;

let boxIndex = 1;

// packageFilter defaults to 'ALL' (no filtering) — only Beyond CBM's package
// dropdown ever passes a specific item id, so console's rendering is
// unaffected by construction, not by a branch. A pallet box's own `item` is
// the pallet's id (a different namespace from package ids), so filtering to
// a specific package also hides pallets — an intentional "x-ray just this
// package type" view, not an oversight. highlighted defaults to false for
// the same reason — only Beyond CBM's legend hover ever sets it.
const PalletizedContainer = ({ container, entities, zValue, animationProgress, edgeColor = 'red', packageFilter = 'ALL', highlighted = false }) => {
  const containerDetails = useMemo(() => {
    const key = Object.keys(entities.containers).find((k) => k === container.id);
    return key ? entities.containers[key] : null;
  }, [container, entities]);

  const containerHeight = (containerDetails?.height || 0) / UNIT_DIVISOR;
  const containerWidth = (containerDetails?.width || 0) / UNIT_DIVISOR;
  const containerLength = (containerDetails?.length || 0) / UNIT_DIVISOR;

  const boxes = useMemo(() => {
    boxIndex = 1;

    const transformedL0Pallets = container.pallets?.map((pallet) => {
      if (!entities.pallets) return null;
      const palletKey = Object.keys(entities.pallets).find((k) => k === pallet.id);
      if (!palletKey) return null;
      const palletDetails = entities.pallets[palletKey];
      if (!palletDetails) return null;

      return pallet.setup
        .filter((s) => s.coordinates[2] === 0)
        .map((setup, itemIndex) => {
          const getDim = (side) => {
            const v = setup.orientation[side];
            if (v === 'length') return palletDetails.length;
            if (v === 'width') return palletDetails.width;
            if (v === 'height') return palletDetails.height;
            return 0;
          };
          const currentBoxIndex = boxIndex;
          const coordinates = [setup.coordinates[0], setup.coordinates[1], (setup.coordinates[2] + getDim('height')) / UNIT_DIVISOR];
          return { index: itemIndex, dimensions: { length: getDim('length'), height: getDim('height'), width: getDim('width') }, coordinates, boxIndex: currentBoxIndex, containerHeight, color: '#cda678', item: pallet.id };
        });
    });

    boxIndex++;

    const transformedL0Items = container.pallets?.map((pallet) => {
      if (!entities.pallets) return null;
      const palletKey = Object.keys(entities.pallets).find((k) => k === pallet.id);
      if (!palletKey) return null;
      const palletDetails = entities.pallets[palletKey];

      return Array.from({ length: pallet.setup.length })
        .filter((_, si) => pallet.setup[si].coordinates[2] === 0)
        .map((_, si) => {
          const baseX = pallet.setup[si].coordinates[0];
          const baseY = pallet.setup[si].coordinates[1];
          const baseZ = (pallet.setup[si].coordinates[2] + palletDetails.height) / UNIT_DIVISOR;
          const palletOriented = pallet.setup[si].orientation.length === 'length';

          return pallet.stacks.filter((s) => s.length > 0).map((stackArray) => {
            const stack = stackArray[0];
            const itemKey = Object.keys(entities.items).find((k) => k === stack.item);
            if (!itemKey) return null;
            const itemDetails = entities.items[itemKey];

            const getDim = (side) => {
              const v = stack.orientation[side];
              if (palletOriented) {
                if (v === 'length') return itemDetails.length;
                if (v === 'width') return itemDetails.width;
              } else {
                if (v === 'length') return itemDetails.width;
                if (v === 'width') return itemDetails.length;
              }
              if (v === 'height') return itemDetails.height;
              return 0;
            };

            const iL = getDim('length'), iH = getDim('height'), iW = getDim('width');

            return Array.from({ length: stack.quantity }).map((_, ii) => {
              const currentBoxIndex = boxIndex;
              let coords = [stack.coordinates[0] + baseX, stack.coordinates[1] + baseY, stack.coordinates[2] + (ii + 1) * (iH / UNIT_DIVISOR) + baseZ];
              if (!palletOriented) coords = [stack.coordinates[1] + baseX, stack.coordinates[0] + baseY, coords[2]];
              boxIndex++;
              return { index: ii, dimensions: { length: iL, height: iH, width: iW }, coordinates: coords, boxIndex: currentBoxIndex, containerHeight, color: itemDetails?.color || 'blue', item: stack.item };
            });
          });
        });
    });

    boxIndex--;

    const transformedL1Pallets = container.pallets?.map((pallet) => {
      if (!entities.pallets) return null;
      const palletKey = Object.keys(entities.pallets).find((k) => k === pallet.id);
      if (!palletKey) return null;
      const palletDetails = entities.pallets[palletKey];
      if (!palletDetails) return null;

      return pallet.setup
        .filter((s) => s.coordinates[2] !== 0)
        .map((setup, itemIndex) => {
          const getDim = (side) => {
            const v = setup.orientation[side];
            if (v === 'length') return palletDetails.length;
            if (v === 'width') return palletDetails.width;
            if (v === 'height') return palletDetails.height;
            return 0;
          };
          const currentBoxIndex = boxIndex;
          const coordinates = [setup.coordinates[0], setup.coordinates[1], (setup.coordinates[2] + getDim('height')) / UNIT_DIVISOR];
          return { index: itemIndex, dimensions: { length: getDim('length'), height: getDim('height'), width: getDim('width') }, coordinates, boxIndex: currentBoxIndex, containerHeight, color: '#cda678', item: pallet.id };
        });
    });

    boxIndex++;

    const transformedL1Items = container.pallets?.map((pallet) => {
      if (!entities.pallets) return null;
      const palletKey = Object.keys(entities.pallets).find((k) => k === pallet.id);
      if (!palletKey) return null;
      const palletDetails = entities.pallets[palletKey];

      return Array.from({ length: pallet.setup.length })
        .filter((_, si) => pallet.setup[si].coordinates[2] !== 0)
        .map((_, si) => {
          const baseX = pallet.setup[si].coordinates[0];
          const baseY = pallet.setup[si].coordinates[1];
          const baseZ = (pallet.setup[si].coordinates[2] + palletDetails.height) / UNIT_DIVISOR;
          const palletOriented = pallet.setup[si].orientation.length === 'length';

          return pallet.stacks.filter((s) => s.length > 0).map((stackArray) => {
            const stack = stackArray[0];
            const itemKey = Object.keys(entities.items).find((k) => k === stack.item);
            if (!itemKey) return null;
            const itemDetails = entities.items[itemKey];

            const getDim = (side) => {
              const v = stack.orientation[side];
              if (palletOriented) {
                if (v === 'length') return itemDetails.length;
                if (v === 'width') return itemDetails.width;
              } else {
                if (v === 'length') return itemDetails.width;
                if (v === 'width') return itemDetails.length;
              }
              if (v === 'height') return itemDetails.height;
              return 0;
            };

            const iL = getDim('length'), iH = getDim('height'), iW = getDim('width');

            return Array.from({ length: stack.quantity }).map((_, ii) => {
              const currentBoxIndex = boxIndex;
              let coords = [stack.coordinates[0] + baseX, stack.coordinates[1] + baseY, stack.coordinates[2] + (ii + 1) * (iH / UNIT_DIVISOR) + baseZ];
              if (!palletOriented) coords = [stack.coordinates[1] + baseX, stack.coordinates[0] + baseY, coords[2]];
              boxIndex++;
              return { index: ii, dimensions: { length: iL, height: iH, width: iW }, coordinates: coords, boxIndex: currentBoxIndex, containerHeight, color: itemDetails?.color || 'blue', item: stack.item };
            });
          });
        });
    });

    return [
      ...(transformedL0Items?.flat()?.flat() || []),
      ...(transformedL0Pallets || []),
      ...(transformedL1Items?.flat()?.flat() || []),
      ...(transformedL1Pallets || []),
    ].flat();
  }, [container, entities, containerHeight]);

  const deferredProgress = useDeferredValue(animationProgress);

  const mergedBoxes = useMemo(() => {
    const geometries = [];
    boxes.forEach((box) => {
      if (box && box.boxIndex <= deferredProgress && (packageFilter === 'ALL' || box.item === packageFilter)) {
        const { geometry } = getBoxMeshData({ dimensions: box.dimensions, coordinates: box.coordinates, color: box.color });
        geometries.push(geometry);
      }
    });
    if (!geometries.length) return null;
    const mergedGeometry = mergeGeometries(geometries, false);
    const material = new THREE.MeshBasicMaterial({ vertexColors: true });
    return { geometry: mergedGeometry, material };
  }, [boxes, deferredProgress, packageFilter]);

  if (!containerDetails) return null;

  return (
    <group position={[0, 0, -zValue]}>
      <mesh position={[containerLength / 2, containerHeight / 2, containerWidth / 2]}>
        <boxGeometry args={[containerLength, containerHeight, containerWidth]} />
        <meshStandardMaterial transparent opacity={0.01} />
        <Edges linewidth={highlighted ? 2.5 : 1} threshold={15} color={highlighted ? '#fbbf24' : edgeColor} />
      </mesh>

      <group
        position={[containerLength + 0.2, containerHeight / 2, containerWidth / 2]}
        rotation={[0, Math.PI / 2, 0]}
      >
        <mesh>
          <planeGeometry args={[containerHeight / UNIT_DIVISOR, containerWidth / UNIT_DIVISOR]} />
          <meshBasicMaterial color="black" side={THREE.DoubleSide} transparent opacity={1} />
        </mesh>
        {/* Explicit local font — see HandStuffedContainer.js for why: without
            one, troika-three-text's CDN font-resolution fetch gets blocked
            by react-snap's skipThirdPartyRequests and crashes the prerender
            build with an uncaught exception. */}
        <Text
          position={[0, 0, 0.01]}
          font="/fonts/SpaceGrotesk-Bold.ttf"
          fontSize={(containerHeight / UNIT_DIVISOR) * 0.1}
          maxWidth={(containerHeight / UNIT_DIVISOR) * 0.9}
          lineHeight={1.6}
          textAlign="center"
          color="white"
          anchorX="center"
          anchorY="middle"
        >
          {`${container.id}\n(x${container.quantity})`}
        </Text>
      </group>

      <mesh position={[containerLength / 2, containerHeight / 2, containerWidth / 2]}>
        <boxGeometry args={[containerLength, containerHeight, containerWidth]} />
        <meshBasicMaterial color={highlighted ? '#fbbf24' : 0xff9999} transparent opacity={highlighted ? 0.22 : 0.08} side={THREE.DoubleSide} />
      </mesh>

      {mergedBoxes && <mesh geometry={mergedBoxes.geometry} material={mergedBoxes.material} />}
      {mergedBoxes && (
        <lineSegments
          geometry={new THREE.EdgesGeometry(mergedBoxes.geometry)}
          material={new THREE.LineBasicMaterial({ color: 'black' })}
        />
      )}
    </group>
  );
};

export default PalletizedContainer;
